import { ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Field, Label } from '../../catalyst/fieldset';
import { Select } from '../../catalyst/select';
import { PageHeader } from '../../components/PageHeader';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { AUDIT_CHECK_IDS, withAuditCheckParam } from './auditCheckIds';
import { AuditSession } from './AuditSession';
import { useAuditSample } from './useAuditSample';

/** The check filter. An id that is not in the list still shows, so a bad link stays visible. */
export function AuditCheckSelect({ value, onChange }: { value: string; onChange: (checkId: string) => void }) {
  const known = (AUDIT_CHECK_IDS as readonly string[]).includes(value);
  return (
    <Field className="mb-4 max-w-md">
      <Label htmlFor="audit-check">Check</Label>
      <Select id="audit-check" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">All checks</option>
        {!known && value ? <option value={value}>{value}</option> : null}
        {AUDIT_CHECK_IDS.map((id) => (
          <option key={id} value={id}>
            {id}
          </option>
        ))}
      </Select>
    </Field>
  );
}

/** `/supervisor/audit`: a reviewer labels a stratified sample of supervisor findings (phase 5). */
export default function SupervisorAudit() {
  useDocumentTitle('Supervisor audit');
  const [searchParams, setSearchParams] = useSearchParams();
  const checkId = searchParams.get('checkId') ?? '';
  const [round, setRound] = useState(0);
  const sample = useAuditSample(round, checkId);
  const onCheckChange = (next: string) => {
    setSearchParams((prev) => withAuditCheckParam(prev, next));
    setRound(0);
  };
  return (
    <div>
      <PageHeader
        title="Supervisor audit"
        description="Label sampled findings: is the discrepancy real, is the proposed value correct, would a lessor want to know."
        icon={<ClipboardDocumentCheckIcon className="size-6" />}
      />
      <AuditCheckSelect value={checkId} onChange={onCheckChange} />
      {sample.isLoading && <p className="text-sm text-gray-500 dark:text-zinc-400">Loading the sample…</p>}
      {sample.isError && <p className="text-sm text-red-600 dark:text-red-400">Could not load the sample.</p>}
      {sample.data && (
        <AuditSession key={`${checkId}:${round}`} items={sample.data.items} onLoadMore={() => setRound((r) => r + 1)} />
      )}
    </div>
  );
}
